///
/// EarthBrowser
///
/// Created by Matt Giger on 10/27/2026
/// Copyright (c) 2026 Geodata Labs
///
/// NHC 120 h wind speed probabilities: sampling and palette shared by the
/// globe swath (StormWindProbabilityNode), the storm column floor and the
/// HUD. The contours are nested exterior rings per threshold and class, so
/// the probability at a point is the highest class whose ring contains it.
///

import { rgba } from "@stovokor/math/color"
import { type WindProbability, type WindProbabilityPolygon } from "./HurricanesTable"

export type WindProbabilityThreshold = 34 | 50 | 64
export const cWindProbabilityThresholds: WindProbabilityThreshold[] = [34, 50, 64]

export function thresholdLabel(kt: WindProbabilityThreshold): string {
	return kt === 34 ? "Tropical-storm force" : kt === 50 ? "50 kt" : "Hurricane force"
}

/** Ray-casting point-in-polygon on a closed [lon, lat] ring */
export function ringContains(ring: number[][], lon: number, lat: number): boolean {
	let inside = false
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i]
		const [xj, yj] = ring[j]
		if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside
	}
	return inside
}

/** Probability (%) that winds of `kt` occur at the point within 120 h; 0 outside every contour */
export function probabilityAt(wp: WindProbability, kt: WindProbabilityThreshold, lat: number, lon: number): number {
	let best = 0
	for (const p of wp.polygons) {
		if (p.kt !== kt || p.pct <= best) continue
		if (ringContains(p.ring, lon, lat)) best = p.pct
	}
	return best
}

export function polygonsFor(wp: WindProbability, kt: WindProbabilityThreshold): WindProbabilityPolygon[] {
	return wp.polygons.filter(p => p.kt === kt).sort((a, b) => a.pct - b.pct)
}

/** Highest class present for a threshold (0 when the product has none) */
export function maxProbability(wp: WindProbability, kt: WindProbabilityThreshold): number {
	let best = 0
	for (const p of wp.polygons) if (p.kt === kt && p.pct > best) best = p.pct
	return best
}

/**
 * NHC-style sequential ramp: pale yellow for 5 % through orange and red to
 * magenta at 100 %. Returned as 0..1 rgb so callers pick the alpha
 */
export function probabilityColor(pct: number): { r: number; g: number; b: number } {
	const stops: Array<[number, number, number, number]> = [
		[5, 1.00, 0.95, 0.60],
		[20, 1.00, 0.80, 0.30],
		[40, 1.00, 0.55, 0.15],
		[60, 0.95, 0.30, 0.15],
		[80, 0.80, 0.15, 0.35],
		[100, 0.55, 0.10, 0.55],
	]
	if (pct <= stops[0][0]) return { r: stops[0][1], g: stops[0][2], b: stops[0][3] }
	for (let i = 1; i < stops.length; i++) {
		if (pct <= stops[i][0]) {
			const [p0, r0, g0, b0] = stops[i - 1]
			const [p1, r1, g1, b1] = stops[i]
			const f = (pct - p0) / (p1 - p0)
			return { r: r0 + (r1 - r0) * f, g: g0 + (g1 - g0) * f, b: b0 + (b1 - b0) * f }
		}
	}
	const last = stops[stops.length - 1]
	return { r: last[1], g: last[2], b: last[3] }
}

export function probabilityHex(pct: number): string {
	const c = probabilityColor(pct)
	const h = (v: number) => Math.round(v * 255).toString(16).padStart(2, "0")
	return `#${h(c.r)}${h(c.g)}${h(c.b)}`
}

/**
 * Fill for one nested band on the globe. The bands stack (every higher
 * class lies inside the lower ones), so a small per-band alpha composites
 * into the familiar darkening toward the track
 */
export function probabilityFill(pct: number, focused: boolean) {
	const c = probabilityColor(pct)
	return rgba(c.r, c.g, c.b, focused ? 0.09 : 0.05)
}
